import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faPlus, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { BIZDEV_NAV, PROPOSAL_COLORS } from './nav';

const STATUSES = ['draft', 'sent', 'under_review', 'accepted', 'declined', 'withdrawn'];

const Proposals: React.FC = () => {
    const { api } = useInternalApi();
    const [proposals, setProposals] = useState<any[]>([]);
    const [byStatus, setByStatus] = useState<any[]>([]);
    const [rate, setRate] = useState<number | null>(null);
    const [leads, setLeads] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ lead_id: '', title: '', summary: '', document_url: '', decision_expected: '' });

    useEffect(() => { document.title = 'Proposals & RFPs - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('bizdev-api/proposals/');
            if (r.data?.status === 'success') {
                setProposals(r.data.data.proposals || []);
                setByStatus(r.data.data.by_status || []);
                setRate(r.data.data.acceptance_rate_pct);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('bizdev-api/leads/?open_only=true').then(r => {
            if (r.data?.status === 'success') setLeads(r.data.data.leads || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.lead_id || !form.title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Choose a lead and give the proposal a title.' });
            return;
        }
        try {
            await api.post('bizdev-api/proposals/', form);
            setShowAdd(false);
            setForm({ lead_id: '', title: '', summary: '', document_url: '', decision_expected: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create proposal.' });
        }
    };

    const setStatus = async (p: any, status: string) => {
        try {
            await api.patch(`bizdev-api/proposals/${p.id}/`, { status });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update proposal.' });
        }
    };

    return (
        <InternalShell activeDashboard="business_development" subNav={BIZDEV_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileSignature} className="me-2 text-primary" />Proposals & RFPs</h2>
                    <p className="text-muted mb-0">Proposals sent and how they landed</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Proposal</button>
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap">
                {byStatus.map(s => (
                    <span key={s.status} className={`badge bg-${PROPOSAL_COLORS[s.status] || 'secondary'} p-2`}>{s.status.replace('_', ' ')}: {s.count}</span>
                ))}
                {rate !== null && <span className="badge bg-light text-dark border p-2">Acceptance rate: {rate}%</span>}
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : proposals.length === 0 ? (
                        <div className="text-center py-5 text-muted">No proposals yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Proposal</th><th>Lead</th><th>Status</th><th>Sent</th><th>Decision due</th><th>Move to</th></tr></thead>
                            <tbody>
                                {proposals.map(p => (
                                    <tr key={p.id}>
                                        <td>
                                            <strong>{p.title}</strong>
                                            {p.document_url && <a href={p.document_url} target="_blank" rel="noreferrer" className="ms-2 small"><FontAwesomeIcon icon={faExternalLinkAlt} /></a>}
                                            <div className="small text-muted">{p.summary}</div>
                                        </td>
                                        <td className="small">{p.lead_name}</td>
                                        <td><span className={`badge bg-${PROPOSAL_COLORS[p.status]}`}>{p.status.replace('_', ' ')}</span></td>
                                        <td className="small">{p.sent_on || '—'}</td>
                                        <td className="small">{p.decision_expected || '—'}</td>
                                        <td style={{ minWidth: 160 }}>
                                            <select className="form-select form-select-sm" value={p.status} onChange={(e) => setStatus(p, e.target.value)}>
                                                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Proposal</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Lead</label>
                                    <select className="form-select" value={form.lead_id} onChange={(e) => setForm(f => ({ ...f, lead_id: e.target.value }))}>
                                        <option value="">Select a lead…</option>
                                        {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                    {leads.length === 0 && <div className="form-text text-warning">No open leads — create one on the Pipeline page first.</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Summary</label>
                                    <textarea className="form-control" rows={3} value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-7">
                                        <label className="form-label">Document link</label>
                                        <input className="form-control" placeholder="https://…" value={form.document_url}
                                            onChange={(e) => setForm(f => ({ ...f, document_url: e.target.value }))} />
                                    </div>
                                    <div className="col-5">
                                        <label className="form-label">Decision due</label>
                                        <input type="date" className="form-control" value={form.decision_expected}
                                            onChange={(e) => setForm(f => ({ ...f, decision_expected: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Proposals;
